/**
 * Theme Toggle Component
 *
 * Button to toggle between light, dark, and system themes.
 */

import { useState } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { clsx } from 'clsx';

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
}

export function ThemeToggle({ className = '', showLabel = false }: ThemeToggleProps) {
  const { theme, setTheme, toggleTheme, isDark } = useTheme();
  const [showMenu, setShowMenu] = useState(false);

  // Simple toggle button (no menu)
  if (!showLabel) {
    return (
      <button
        onClick={toggleTheme}
        className={clsx(
          'p-2 rounded-lg transition-colors',
          'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
          'dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800',
          className
        )}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label="Toggle theme"
      >
        {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>
    );
  }

  // Full menu with all options
  return (
    <div className={clsx('relative', className)}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
          'text-gray-600 hover:bg-gray-100',
          'dark:text-gray-300 dark:hover:bg-gray-800'
        )}
      >
        {theme === 'dark' ? (
          <Moon className="h-4 w-4" />
        ) : theme === 'light' ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Monitor className="h-4 w-4" />
        )}
        <span className="text-sm capitalize">{theme}</span>
      </button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          <div
            className={clsx(
              'absolute right-0 top-full z-50 mt-2 w-40 rounded-lg shadow-lg border',
              'bg-white border-gray-200',
              'dark:bg-gray-900 dark:border-gray-700'
            )}
          >
            <div className="p-1">
              <ThemeOption
                icon={Sun}
                label="Light"
                selected={theme === 'light'}
                onClick={() => {
                  setTheme('light');
                  setShowMenu(false);
                }}
              />
              <ThemeOption
                icon={Moon}
                label="Dark"
                selected={theme === 'dark'}
                onClick={() => {
                  setTheme('dark');
                  setShowMenu(false);
                }}
              />
              <ThemeOption
                icon={Monitor}
                label="System"
                selected={theme === 'system'}
                onClick={() => {
                  setTheme('system');
                  setShowMenu(false);
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface ThemeOptionProps {
  icon: typeof Sun;
  label: string;
  selected: boolean;
  onClick: () => void;
}

function ThemeOption({ icon: Icon, label, selected, onClick }: ThemeOptionProps) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
        selected
          ? 'bg-hermit-100 text-hermit-700 dark:bg-hermit-900 dark:text-hermit-300'
          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
      )}
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1 text-left">{label}</span>
      {selected && <Check className="h-4 w-4" />}
    </button>
  );
}
